import { Fragment, useMemo, useState } from 'react';
import type { OpportunityForecast } from '@catalyst-psa/shared';

import { Spinner } from '../../components/ui/Spinner.js';
import { useOpportunityForecast, type OpportunityForecastParams } from './api.js';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const currencyCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Formats a 'YYYY-MM' month key as e.g. "Jul 2026". */
function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Date.UTC(Number(year), Number(m) - 1, 1));
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function money(value: string, withCents = false): string {
  return (withCents ? currencyCents : currency).format(Number(value));
}

interface OpportunityForecastPanelProps {
  /** Opens the source opportunity (by id) when a drill-down row is clicked. Omit for read-only. */
  onOpenOpportunity?: (opportunityId: string) => void;
  /** Pre-fetched forecast (e.g. the public portal). When provided, the panel does not fetch. */
  data?: OpportunityForecast;
  /** List filters to apply so the forecast matches the opportunities list below it. */
  filters?: OpportunityForecastParams;
  /**
   * Moves an opportunity's close date to the last day of the given 'YYYY-MM' month (from a
   * drag-and-drop of the row's grip handle). Omit to disable dragging.
   */
  onMoveCloseMonth?: (opportunityId: string, month: string) => void;
  /** Marks an opportunity Lost - Project Cancelled from its drill-down row. Omit to hide the action. */
  onMarkLost?: (opportunityId: string) => void;
}

/**
 * A collapsible, interactive future-revenue forecast above the opportunities list.
 * Rows are "Types" (product service categories); columns are close-spread months.
 * Clicking a Type row drills down to its contributing opportunities (aligned under the
 * month they fall in); clicking an opportunity opens it.
 */
export function OpportunityForecastPanel({
  onOpenOpportunity,
  data,
  filters,
  onMoveCloseMonth,
  onMarkLost,
}: OpportunityForecastPanelProps): JSX.Element {
  const query = useOpportunityForecast(filters ?? {}, data === undefined);
  const [collapsed, setCollapsed] = useState(true);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  /** The opportunity id currently being dragged by its close-month grip, or null. */
  const [draggingOppId, setDraggingOppId] = useState<string | null>(null);
  /** The `${oppId}|${month}` cell currently under the drag, for a drop-target highlight. */
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);

  const forecast: OpportunityForecast | undefined = data ?? query.data;
  const isLoading = data === undefined && query.isLoading;
  const isError = data === undefined && query.isError;

  const toggleType = (type: string): void => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const grandTotal = forecast?.total ?? '0';

  // Contributions grouped per type by opportunity, so a drill-down row spans the months.
  const contributionsByType = useMemo(() => {
    const map = new Map<
      string,
      Map<
        string,
        {
          opportunityId: string;
          opportunityName: string;
          customerName: string;
          salesRepName: string | null;
          byMonth: Record<string, string>;
          total: number;
        }
      >
    >();
    for (const type of forecast?.types ?? []) {
      const byOpp = new Map<
        string,
        {
          opportunityId: string;
          opportunityName: string;
          customerName: string;
          salesRepName: string | null;
          byMonth: Record<string, string>;
          total: number;
        }
      >();
      for (const c of type.contributions) {
        let row = byOpp.get(c.opportunityId);
        if (!row) {
          row = {
            opportunityId: c.opportunityId,
            opportunityName: c.opportunityName,
            customerName: c.customerName,
            salesRepName: c.salesRepName,
            byMonth: {},
            total: 0,
          };
          byOpp.set(c.opportunityId, row);
        }
        row.byMonth[c.month] = c.amount;
        row.total += Number(c.amount);
      }
      map.set(type.type, new Map([...byOpp.entries()].sort((a, b) => b[1].total - a[1].total)));
    }
    return map;
  }, [forecast]);

  const months = forecast?.months ?? [];

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2">
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? '' : 'rotate-90'}`}
          >
            <path
              fillRule="evenodd"
              d="M7.21 4.21a.75.75 0 011.06 0l5 5a.75.75 0 010 1.06l-5 5a.75.75 0 11-1.06-1.06L11.69 10 7.21 5.27a.75.75 0 010-1.06z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm font-semibold text-slate-900">Pipeline Forecast</span>
          <span className="text-xs text-slate-500">by type &amp; month</span>
        </span>
        <span className="text-sm font-semibold text-slate-900">
          {forecast ? money(grandTotal, true) : ''}
        </span>
      </button>

      {collapsed ? null : (
        <div className="border-t border-slate-200">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-slate-500">
              <Spinner className="h-4 w-4" /> Loading forecast…
            </div>
          ) : isError ? (
            <div className="px-4 py-6 text-sm text-rose-600">Failed to load the forecast.</div>
          ) : !forecast || forecast.types.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-500">
              No forecastable opportunities yet. Opportunities need a close date and product revenue
              to appear here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="sticky left-0 z-10 bg-white px-4 py-2 font-medium">Type</th>
                    {months.map((m) => (
                      <th key={m} className="whitespace-nowrap px-3 py-2 text-right font-medium">
                        {formatMonth(m)}
                      </th>
                    ))}
                    <th className="whitespace-nowrap px-4 py-2 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {forecast.types.map((type) => {
                    const isOpen = expandedTypes.has(type.type);
                    const oppRows = contributionsByType.get(type.type);
                    return (
                      <Fragment key={type.type}>
                        <tr
                          key={type.type}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => toggleType(type.type)}
                        >
                          <td className="sticky left-0 z-10 bg-white px-4 py-2 font-medium text-slate-900">
                            <span className="inline-flex items-center gap-1.5">
                              <svg
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.21 4.21a.75.75 0 011.06 0l5 5a.75.75 0 010 1.06l-5 5a.75.75 0 11-1.06-1.06L11.69 10 7.21 5.27a.75.75 0 010-1.06z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {type.type}
                            </span>
                          </td>
                          {months.map((m) => (
                            <td
                              key={m}
                              className="whitespace-nowrap px-3 py-2 text-right text-slate-600"
                            >
                              {type.totalsByMonth[m] ? money(type.totalsByMonth[m]) : '—'}
                            </td>
                          ))}
                          <td className="whitespace-nowrap px-4 py-2 text-right font-semibold text-slate-900">
                            {money(type.total)}
                          </td>
                        </tr>
                        {isOpen && oppRows
                          ? [...oppRows.values()].map((row) => {
                              const firstMonth = months.find((m) => row.byMonth[m]);
                              const isDragging = draggingOppId === row.opportunityId;
                              return (
                              <tr
                                key={`${type.type}|${row.opportunityId}`}
                                className={`bg-slate-50/40 ${onOpenOpportunity ? 'cursor-pointer hover:bg-brand-50' : ''} ${isDragging ? 'opacity-50' : ''}`}
                                onClick={
                                  onOpenOpportunity
                                    ? () => onOpenOpportunity(row.opportunityId)
                                    : undefined
                                }
                              >
                                <td className="sticky left-0 z-10 bg-inherit px-4 py-1.5 pl-9 text-slate-600">
                                  <span className="inline-flex items-center gap-1.5">
                                    <span
                                      className={
                                        onOpenOpportunity
                                          ? 'font-medium text-brand-700'
                                          : 'font-medium text-slate-700'
                                      }
                                    >
                                      {row.opportunityName}
                                    </span>
                                    <span className="text-slate-400"> · {row.customerName}</span>
                                    {row.salesRepName ? (
                                      <span className="text-slate-400"> · {row.salesRepName}</span>
                                    ) : null}
                                    {onMarkLost ? (
                                      <button
                                        type="button"
                                        title="Mark this opportunity Lost - Project Cancelled"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onMarkLost(row.opportunityId);
                                        }}
                                        className="ml-1 inline-flex items-center gap-0.5 rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-rose-700"
                                      >
                                        <svg viewBox="0 0 20 20" fill="currentColor" className="h-2.5 w-2.5">
                                          <path
                                            fillRule="evenodd"
                                            d="M10 8.586l3.293-3.293a1 1 0 111.414 1.414L11.414 10l3.293 3.293a1 1 0 01-1.414 1.414L10 11.414l-3.293 3.293a1 1 0 01-1.414-1.414L8.586 10 5.293 6.707a1 1 0 011.414-1.414L10 8.586z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                        Lost
                                      </button>
                                    ) : null}
                                  </span>
                                </td>
                                {months.map((m) => {
                                  const cellKey = `${row.opportunityId}|${m}`;
                                  const droppable = Boolean(onMoveCloseMonth);
                                  return (
                                    <td
                                      key={m}
                                      className={`whitespace-nowrap px-3 py-1.5 text-right text-slate-500 ${dragOverCell === cellKey ? 'bg-brand-100 ring-1 ring-inset ring-brand-300' : ''}`}
                                      onDragOver={
                                        droppable
                                          ? (e) => {
                                              e.preventDefault();
                                              e.dataTransfer.dropEffect = 'move';
                                              if (dragOverCell !== cellKey) setDragOverCell(cellKey);
                                            }
                                          : undefined
                                      }
                                      onDragLeave={
                                        droppable
                                          ? () =>
                                              setDragOverCell((prev) =>
                                                prev === cellKey ? null : prev,
                                              )
                                          : undefined
                                      }
                                      onDrop={
                                        droppable
                                          ? (e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              const oppId = draggingOppId;
                                              setDragOverCell(null);
                                              setDraggingOppId(null);
                                              if (oppId && onMoveCloseMonth)
                                                onMoveCloseMonth(oppId, m);
                                            }
                                          : undefined
                                      }
                                    >
                                      <span className="inline-flex items-center justify-end gap-1">
                                        {droppable && m === firstMonth ? (
                                          <span
                                            draggable
                                            title="Drag to a different month to change the close date"
                                            onClick={(e) => e.stopPropagation()}
                                            onDragStart={(e) => {
                                              e.stopPropagation();
                                              e.dataTransfer.effectAllowed = 'move';
                                              e.dataTransfer.setData('text/plain', row.opportunityId);
                                              setDraggingOppId(row.opportunityId);
                                            }}
                                            onDragEnd={() => {
                                              setDraggingOppId(null);
                                              setDragOverCell(null);
                                            }}
                                            className="cursor-grab text-slate-400 hover:text-brand-600 active:cursor-grabbing"
                                          >
                                            <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
                                              <path d="M7 4a1 1 0 100 2 1 1 0 000-2zM7 9a1 1 0 100 2 1 1 0 000-2zM7 14a1 1 0 100 2 1 1 0 000-2zM13 4a1 1 0 100 2 1 1 0 000-2zM13 9a1 1 0 100 2 1 1 0 000-2zM13 14a1 1 0 100 2 1 1 0 000-2z" />
                                            </svg>
                                          </span>
                                        ) : null}
                                        {row.byMonth[m] ? money(row.byMonth[m]) : ''}
                                      </span>
                                    </td>
                                  );
                                })}
                                <td className="whitespace-nowrap px-4 py-1.5 text-right text-slate-600">
                                  {money(String(row.total))}
                                </td>
                              </tr>
                              );
                            })
                          : null}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="border-t-2 border-slate-200 text-sm font-semibold text-slate-900">
                  <tr>
                    <td className="sticky left-0 z-10 bg-white px-4 py-2">Total</td>
                    {months.map((m) => (
                      <td key={m} className="whitespace-nowrap px-3 py-2 text-right">
                        {forecast.totalsByMonth[m] ? money(forecast.totalsByMonth[m]) : '—'}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-4 py-2 text-right">{money(grandTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
